/** @jsxImportSource @emotion/react */
import React, { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import { CellType } from '@grapecity/wijmo.grid';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useMessageBar } from 'hooks/useMessageBar';
import { useCommonModal } from 'hooks/useCommonModal';
import { ControlBtnGroup, SubTitleGroup, SubTitleLayout } from 'components/layouts/ContentLayout';
import { tabs } from 'components/layouts/Tab';
import { tb } from 'components/layouts/Table';
import { IconButton } from 'components/buttons/IconSVG';
import { findMpItemCop, findPlantHorExplCrgeUserCount } from 'apis/mp/MpItemCop';
import { saveFactory, saveFactoryList, saveFactoryStat } from 'apis/mp/MpFactory';
import { findFactory, findMpResults, generateAtchFileGrId } from 'apis/mp/MpResult';
import { MpItemCop } from 'models/mp/MpItemCop';
import { MpResultCondition } from 'models/mp/MpResult';
import { CrudCode } from 'models/common/Edit';
import FileUploadPopUp from 'pages/common/components/FileUploadPopUp';
import CustomGrid from 'components/grids/CustomGrid';
import { MpFactoryPopUp } from '../popup/MpFactoryPopUp';
import { MpPlntList } from 'models/mp/MpPlnt';
import MpItemOtherContentEquipmentGrid from './MpItemOtherContentEquipmentGrid';
import EquipmentPopUp from '../../common/popup/EquipmentPopUp';
import { CustomEquipment, EquipmentLevel } from 'models/common/popup/EquipmentMaster';
import { ItemStatCd, MpItem, MpItemTpCd } from 'models/mp/MpItem';
import { GatingContListPopUp } from '../../gtng/popup/GatingContListPopUp';
import { MpMgrChgHistoryPopUp } from '../popup/MpMgrChgHistoryPopUp';
import { open4mcc } from 'utils/ApiUtil';
import useSessionStore from 'stores/useSessionStore';
import { hasRole } from 'utils/SessionUtil';
import UserMultiModal from 'components/modals/common/UserMultiModal';
import { MpRemarkPopUp } from '../popup/MpRemarkPopUp';

/**
 * @description 수평전개 탭
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 *  25.10.14    jihwan89       설비변경점 개선사항 v5.2 50행
 *                               1) 비대상법인은 Tab에서 보이지 않게 수정
 *                               2) 법인 하위 수평전개 대상, 상태, 진행율, 비대상 사유 숨기기
 *  25.10.29    jihwan89       CSR E20251027-000124 설비추가 시 설비레벨 조건 MAIN 추가(기존: Machine)
 *  25.11.03    jihwan89       설비변경점 개선사항 v5.2 수평전개 담당자 4MCC 확정전 DROP 및 사유입력 추가
 *  25.11.10    jihwan89       Factory 팝업에서 공장 체크 해제 후 선택 시 삭제되도록 밸리데이션 삭제 (PI 요청)
 *  26.02.05    jihwan89       수평전개 진척 현황 탭에서 태그 이동 시 해당 법인으로 이동
 **/

interface Props {
  defaultMpItem: MpItem;
  itemTpCd: string;
  itemId: string;
  mpClsfCds?: string;
  isRefresh?: boolean;
  changeTab?: boolean;
  condition?: {
    itemId?: string;
    itemTpCd?: string;
    copCd?: string;
    plntCd?: string;
    lnId?: string;
  };
  onChangeRefresh: () => void;
  onRefresh: () => void;
}

const MpItemOtherContent = (props: Props, ref) => {
  const { t } = useTranslation();
  const gridRef = useRef<any>();
  const firstRef = useRef(true);
  const refreshRef = useRef(false);
  const equipmentRef = useRef<any>();
  const { openMessageBar } = useMessageBar();
  const { openCommonModal } = useCommonModal();

  const { userId } = useSessionStore();
  const [rowData, setRowData] = useState<MpPlntList[]>([]);
  const [copTabIndex, setCopTabIndex] = useState<number>(0);
  const [mpItemCops, setMpItemCops] = useState<MpItemCop[]>([]);
  // 비대상법인은 Tap에서 보이지 않게 수정 - 설비변경점 개선사항 v5.2
  const [mpItemTgtYnCops, setMpItemTgtYnCops] = useState<MpItemCop[]>([]);

  const [isOpenUserModal, setOpenUserModal] = useState<boolean>(false);
  const [userModalCondition, setUserModalCondition] = useState<any>({});
  const [fileUploadModalCondition, setFileUploadModalCondition] = useState<any>({});
  const [defaultCondition, setDefaultCondition] = useState<MpResultCondition>({});

  const [isOpenMpFactoryModal, setOpenMpFactoryModal] = useState<boolean>(false);
  const [mpFactoryModalCondition, setMpFactoryModalCondition] = useState<any>({});
  const [isOpenFileUploadModal, setOpenFileUploadModal] = useState<boolean>(false);

  const [selectedPlant, setSelectedPlant] = useState<MpPlntList>();
  const [isOpenEquipmentModal, setOpenEquipmentModal] = useState<boolean>(false);
  const [equipmentModalCondition, setEquipmentModalCondition] = useState<any>({});

  const [hasAuth, setHasAuth] = useState<boolean>(false);
  const [hasIfAuth, setHasIfAuth] = useState<boolean>(false);
  const [isOpenUserReadModal, setOpenUserReadModal] = useState<boolean>(false);
  const [userReadModalCondition, setUserReadModalCondition] = useState<any>({});
  const [isOpenMpMgrChgHistoryPopup, setOpenMpMgrChgHistoryPopup] = useState<boolean>(false);
  const [mpMgrChgHistoryPopupCondition, setMpMgrChgHistoryPopupCondition] = useState<any>({});

  const [isOpenMpRemarkPopUp, setOpenMpRemarkPopUp] = useState<boolean>(false);
  const [dropFactoryCondition, setDropFactoryCondition] = useState<any>({});

  const editableFactory = useMemo(() => {
    /**
     * # 수평전개 탭 내 <법인설정><Factory추가> 버튼
     * 1. <Drop><수평전개보류>를 제외한 모든 상태에서 버튼 노출
     * 2. <수평전개완료><완료>인 경우 담당자/분과위/일반사용자에게 노출
     *       - 그 외 상태는 담당자/분과위인 경우 노출
     */
    // <Drop><수평전개보류>를 제외한 모든 상태에서 버튼 노출
    if (
      ItemStatCd.Drop !== props?.defaultMpItem?.itemStatCd &&
      ItemStatCd.HeHold !== props?.defaultMpItem?.itemStatCd
    ) {
      // <수평전개완료><완료>인 경우 담당자/분과위/일반사용자에게 노출
      if (
        ItemStatCd.HeComplete === props?.defaultMpItem?.itemStatCd ||
        ItemStatCd.Complete === props?.defaultMpItem?.itemStatCd
      ) {
        return true;
      } else {
        // 그 외 상태는 담당자/분과위인 경우 노출
        if (
          props?.defaultMpItem?.reqMgrYn === 'Y' ||
          props?.defaultMpItem?.mcrcYn === 'Y' ||
          hasAuth
        ) {
          return true;
        }
      }
    }
    return false;
  }, [
    props?.defaultMpItem?.itemStatCd,
    props?.defaultMpItem?.reqMgrYn,
    props?.defaultMpItem?.mcrcYn,
    hasAuth,
  ]);

  useImperativeHandle(ref, () => ({
    validate: () => {
      return true;
    },
    validateItemStatCd: async (stat) => {
      // 수평전개 or 설비변경점/수평전개인 경우
      if (
        MpItemTpCd.HE === props?.defaultMpItem?.mpItemTpCd ||
        MpItemTpCd.MPHE === props?.defaultMpItem?.mpItemTpCd
      ) {
        if (!props?.defaultMpItem?.itemId) {
          openMessageBar({
            type: 'error',
            content: String(t('mp.label.Item Id 정보가 없습니다.', 'Item Id 정보가 없습니다.')),
          });
          return false;
        }
        // 대상 법인 중 1개 이상의 Factory 필요, 수평전개 담당자 필수
        const cops = await findPlantHorExplCrgeUserCount(props?.defaultMpItem?.itemId);
        if ((cops || []).length < 1) {
          openMessageBar({
            type: 'error',
            content: String(
              t('mp.label.설정된 대상법인이 없습니다.', '설정된 대상법인이 없습니다.')
            ),
          });
          return false;
        }
        const emptyPlnts = (cops || []).filter((o) => Number(o?.plntCount || 0) < 1);
        if (emptyPlnts.length > 0) {
          openMessageBar({
            type: 'error',
            content: `${String(
              t(
                'mp.label.하나 이상의 Factory를 등록해 주세요.',
                '하나 이상의 Factory를 등록해 주세요.'
              )
            )} (${emptyPlnts.map((o) => o.copCd).join(', ')})`,
          });
          return false;
        }
        const users = (cops || []).filter((o) => o.plntCount !== o.horExplCrgeUserCount);
        if (users.length > 0) {
          openMessageBar({
            type: 'error',
            content: `${String(
              t('mp.label.수평전개담당자를 등록해 주세요.', '수평전개담당자를 등록해 주세요.')
            )} (${users.map((o) => o.copCd).join(', ')})`,
          });
          return false;
        }
        // 현재 Item 등록 상태가 아닌 경우 추가된 공장이 있는지 확인
        if (ItemStatCd.Regist !== props?.defaultMpItem?.itemStatCd) {
          if ((cops || []).filter((o) => Number(o?.newPlntCount || 0) > 0).length < 1) {
            openMessageBar({
              type: 'warning',
              content: t('mp.label.추가된 공장이 없습니다.', '추가된 공장이 없습니다.'),
            });
            return false;
          }
        }
        // FastTrack으로 진행 시 설비변경점/수평전개 탭으로 기본사항이 복사된 경우 개조개선담당자는 사용자가 임의로등록해야하므로
        // 법인검토중 되기 전에 밸리데이션 처리
        const resultList = await findMpResults(
          props.defaultMpItem?.itemId,
          props.defaultMpItem?.tgtCopCd || '',
          props.defaultMpItem?.tgtPlntCd || '',
          ''
        );

        const cortCrgeUsers = (resultList || []).filter((o) => _.isEmpty(o.cortCrgeUserId));
        if (MpItemTpCd.MPHE === props?.defaultMpItem?.mpItemTpCd && cortCrgeUsers.length > 0) {
          openMessageBar({
            type: 'warning',
            content: t('mp.label.개조담당자를 등록해 주세요.', '개조담당자를 등록해 주세요.'),
          });
          return false;
        }
      }
      return true;
    },
    getRowData: () => {
      return (rowData || []).filter((o) => o.crudKey);
    },
    refresh: () => {
      console.log('-');
    },
    reload: (itemId: string, itemTpCd: string, copCd: string, plntCd: string) => {
      refreshRef.current = true;
      let isReset = true;
      // 동일한 ItemId에 다른 조건으로 진입한 경우
      if (props.itemId === itemId) {
        const tabIndex = Math.max(
          0,
          (mpItemCops || []).findIndex((o) => o.copCd === copCd)
        );
        if (copTabIndex === tabIndex) {
          isReset = false;
        }
        setCopTabIndex(tabIndex);
      }
    },
    searchMpItemCop: () => {
      if (props.itemId) {
        // 법인 조회
        findMpItemCop(props.itemId)
          .then((result) => {
            // 비대상법인은 Tap에서 보이지 않게 수정 - 설비변경점 개선사항 v5.2
            const cops = (result || []).filter((o) => o.tgtYn === 'Y');
            setMpItemCops(result);
            setMpItemTgtYnCops(cops);
          })
          .finally(() => {
            if (firstRef.current) {
              firstRef.current = false;
            }
          });
      }
    },
  }));

  useEffect(() => {
    const appEnv = `${process.env.REACT_APP_NODE_ENV}` || '';
    const testEnv = ['local', 'dev', 'stg'];
    setHasIfAuth(hasRole('ADM') && testEnv.includes(appEnv));
    setHasAuth(hasRole('ADM'));
  }, []);

  useEffect(() => {
    if (props.isRefresh) {
      props.onChangeRefresh();
      refreshRef.current = true;
      if (
        defaultCondition?.itemId !== props.condition?.itemId ||
        defaultCondition?.itemTpCd !== props.condition?.itemTpCd ||
        defaultCondition?.copCd !== props.condition?.copCd ||
        defaultCondition?.plntCd !== props.condition?.plntCd ||
        defaultCondition?.lnId !== props.condition?.lnId
      ) {
        setDefaultCondition({
          itemId: props.condition?.itemId,
          itemTpCd: props.condition?.itemTpCd,
          copCd: props.condition?.copCd,
          plntCd: props.condition?.plntCd,
          lnId: props.condition?.lnId,
        });
      }
    }
  }, [props.condition, props.isRefresh]);

  useEffect(() => {
    // 법인코드 기본값이 있는 경우 제외
    if (!refreshRef.current) {
      // 수평전개/설비변경/신규개선 탭 변경 시 값 초기화
      setCopTabIndex(0);
    }
    if (props.itemId) {
      // 법인 조회
      findMpItemCop(props.itemId)
        .then((result) => {
          // 비대상법인은 Tap에서 보이지 않게 수정 - 설비변경점 개선사항 v5.2
          const cops = (result || []).filter((o) => o.tgtYn === 'Y');
          setMpItemCops(result);
          setMpItemTgtYnCops(cops);
          if (props.condition && !props.changeTab) {
            setDefaultCondition({
              itemId: props.condition?.itemId,
              itemTpCd: props.condition?.itemTpCd,
              copCd: props.condition?.copCd,
              plntCd: props.condition?.plntCd,
              lnId: props.condition?.lnId,
            });
          }
        })
        .finally(() => {
          if (firstRef.current) {
            firstRef.current = false;
          }
        });
    }
  }, [props.itemId, props.itemTpCd]);

  useEffect(() => {
    if (defaultCondition.copCd) {
      setCopTabIndex(
        Math.max(
          0,
          (mpItemCops || []).findIndex((o) => o.copCd === defaultCondition.copCd)
        )
      );
    }
  }, [mpItemCops, defaultCondition.copCd]);

  useEffect(() => {
    if (!refreshRef.current) {
      // initCondition();
    }
    setRowData([]);
    // 법인 탭 변경 시 공장 목록 조회
    searchFactory(props.itemId || '', mpItemCops[copTabIndex]?.copCd || '', props.itemTpCd).then(
      (result) => {
        const plnt = (result || []) as MpPlntList[];
        setRowData(plnt);
        setSelectedPlant(plnt.length > 0 ? plnt[0] : undefined);
      }
    );
  }, [copTabIndex, mpItemCops]);

  /**
   * 공장(동) 목록 조회
   */
  const searchFactory = (itemId: string, copCd: string, itemTpCd: string) => {
    return new Promise((resolve) => {
      if (itemId && copCd && itemTpCd) {
        // 공장(동)
        findFactory(itemId, copCd, itemTpCd).then((result) => {
          resolve(result);
        });
      } else {
        resolve([]);
      }
    });
  };

  const handleFileUpload = async (item, row, col) => {
    let atchFileGrId = item.atchFileGrId;
    if (_.isEmpty(atchFileGrId)) {
      atchFileGrId = await generateAtchFileGrId(item.execPlanId);
      if (atchFileGrId) {
        item.atchFileGrId = atchFileGrId;
      } else {
        openMessageBar({
          type: 'error',
          content:
            t(
              'com.label.첨부파일그룹ID 생성에 실패했습니다.',
              '첨부파일그룹ID 생성에 실패했습니다.'
            ) +
            <br /> +
            t('com.label.새로고침 후 다시 시도해 주세요.', '새로고침 후 다시 시도해 주세요.'),
        });
        return;
      }
    }
    setFileUploadModalCondition({
      atchFileGrId: atchFileGrId,
      tableName: 'tb_elm_mp_result_n',
      bizName: 'mp',
      target: {
        item: item,
        row: row,
        col: col,
      },
    });
    setOpenFileUploadModal(true);
  };

  const changeFactoryStatByM4Change = async (
    item: MpPlntList,
    buttonId: 'm4Confirm' | 'addHe' | 'reHe'
  ) => {
    if (!item.mpItemId) {
      openMessageBar({
        type: 'error',
        content: t('mp.label.Item ID가 없습니다.', 'Item ID가 없습니다.'),
      });
      return;
    }
    if (!item.copCd) {
      openMessageBar({
        type: 'error',
        content: t('mp.label.법인코드가 없습니다.', '법인코드가 없습니다.'),
      });
      return;
    }
    if (!item.prdnPlntCd) {
      openMessageBar({
        type: 'error',
        content: t('mp.label.공장코드가 없습니다.', '공장코드가 없습니다.'),
      });
      return;
    }
    const eqpList = await findMpResults(
      item.mpItemId,
      item.copCd,
      item.prdnPlntCd,
      item.mpPrdnPlntId
    );
    const valid = eqpList.filter((o) => !o.mpItemStatCd || !o.m4ChgNo).length > 0;
    if (!valid) {
      openMessageBar({
        type: 'warning',
        content: t('mp.label.설비를 등록해 주세요', '설비를 등록해 주세요'),
      });
      return;
    }
    // 대상 법인 중 1개 이상의 Factory 필요, 수평전개 담당자 필수
    const cops = await findPlantHorExplCrgeUserCount(item.mpItemId, item.mpPrdnPlntId);
    const users = (cops || []).filter((o) => (o.horExplCrgeUserCount || 0) < 1);
    if (users.length > 0) {
      openMessageBar({
        type: 'error',
        content: String(
          t('mp.label.수평전개담당자를 등록해 주세요.', '수평전개담당자를 등록해 주세요.')
        ),
      });
      return false;
    }
    openCommonModal({
      modalType: 'confirm',
      content:
        'addHe' === buttonId
          ? t('mp.label.추가수평전개 처리하시겠습니까?', '추가수평전개 처리하시겠습니까?')
          : 'reHe' === buttonId
          ? t('mp.label.재신청 처리하시겠습니까?', '재신청 처리하시겠습니까?')
          : t('mp.label.4M 변경 확정 처리하시겠습니까?', '4M 변경 확정 처리하시겠습니까?'),
      yesCallback: async () => {
        const res = await saveFactoryStat(
          item?.mpItemId,
          item?.copCd,
          item?.prdnPlntCd,
          item?.mpPrdnPlntId,
          ItemStatCd.M4Change
        );
        // 공장 상태 <법인 검토중>|<수평전개완료>|<Drop> -> <4M 변경중>
        if (res.successOrNot === 'Y') {
          openMessageBar({
            type: 'confirm',
            content:
              'addHe' === buttonId
                ? t('mp.label.추가수평전개 처리되었습니다.', '추가수평전개 처리되었습니다.')
                : 'reHe' === buttonId
                ? t('mp.label.재신청 처리되었습니다.', '재신청 처리되었습니다.')
                : t('mp.label.4M 변경 확정 처리되었습니다.', '4M 변경 확정 처리되었습니다.'),
          });
          // Item, 법인 상태 재조회
          props?.onRefresh();
          findMpItemCop(props.itemId).then((result) => {
            // 비대상법인은 Tap에서 보이지 않게 수정 - 설비변경점 개선사항 v5.2
            const cops = (result || []).filter((o) => o.tgtYn === 'Y');
            setMpItemCops(result);
            setMpItemTgtYnCops(cops);
          });
          if (item?.mpItemId && item?.copCd) {
            searchFactory(item?.mpItemId, item?.copCd, props.itemTpCd).then((result) => {
              const plnt = (result || []) as MpPlntList[];
              setRowData(plnt);
              gridRef.current?.refresh();
            });
          }
          // 설비추가한 공장으로 선택 (설비 목록 재조회)
          setSelectedPlant(item);
          equipmentRef.current?.searchMpResult(
            item?.mpItemId,
            item?.copCd,
            item?.prdnPlntCd,
            item?.mpPrdnPlntId
          );
        } else {
          openMessageBar({
            type: 'error',
            content:
              res.statusCode === 'FAIL_IF'
                ? t(
                    'com.label.IF 중 오류가 발생했습니다. 시스템담당자에게 문의하세요.',
                    'IF 중 오류가 발생했습니다. 시스템담당자에게 문의하세요.'
                  )
                : t('com.label.처리 중 오류가 발생했습니다.', '처리 중 오류가 발생했습니다.'),
          });
        }
      },
    });
  };

  const layoutDefinition = useMemo(() => {
    const drawActionButtons = (params) => {
      const buttons = [] as string[];
      // 설비변경점/수평전개, 수평전개 유형인 경우에만 노출
      if (
        MpItemTpCd.MPHE === props?.defaultMpItem?.mpItemTpCd ||
        MpItemTpCd.HE === props?.defaultMpItem?.mpItemTpCd
      ) {
        // 분과위, 수평전개담당자인 경우
        if (
          props?.defaultMpItem?.mcrcYn === 'Y' ||
          params.item?.horExplCrgeUserYn === 'Y' ||
          hasAuth
        ) {
          // <4M 변경중>을 제외한 모든 상태에 설비추가 가능
          if (ItemStatCd.M4Change !== params.item?.mpItemStatCd) {
            buttons.push(
              `<button id="addEqp">${String(t('mp.label.설비추가', '설비추가'))}</button>`
            );
          }
          // Item 등록 단계에서는 Factory 처리 불가
          if (ItemStatCd.Regist !== props?.defaultMpItem?.itemStatCd) {
            if (ItemStatCd.CopReview === params.item?.mpItemStatCd) {
              // 법인검토중: <설비추가><4M 변경확정>
              buttons.push(
                `<button id="m4Confirm">${String(
                  t('mp.label.4M 변경확정', '4M 변경확정')
                )}</button>`
              );
            }
            // TODO 4M 변경중: <수평전개완료><Drop> (IF 처리 전 임시 버튼) - s
            if (ItemStatCd.M4Change === params.item?.mpItemStatCd) {
              if (hasIfAuth) {
                // [25.09.11] DEV, QA환경에서 ADM권한인 경우 노출되도록 수정
                buttons.push(`<button id="cmplHe">4MCC 수평전개완료</button>`);
                buttons.push(`<button id="drop">4MCC Drop</button>`);
              }
            }
            // TODO 4M 변경중: <수평전개완료><Drop> (IF 처리 전 임시 버튼) - e
            // 수평전개완료: <설비추가><추가수평전개>
            if (ItemStatCd.HeComplete === params.item?.mpItemStatCd) {
              buttons.push(
                `<button id="addHe">${String(t('mp.label.추가수평전개', '추가수평전개'))}</button>`
              );
            }
            // Drop: <설비추가><재신청>
            if (ItemStatCd.Drop === params.item?.mpItemStatCd) {
              buttons.push(`<button id="reHe">${String(t('mp.label.재신청', '재신청'))}</button>`);
            }
            // 설비변경점 개선사항 v5.2 - 법인 검토중: <drop> / 권한: 수평전개담당자, 분과위원
            if (ItemStatCd.CopReview === params.item?.mpItemStatCd) {
              buttons.push(
                `<button id="dropCopReview">${String(t('mp.label.DROP', 'DROP'))}</button>`
              );
            }
          }
        }
      }
      return buttons.join(' ');
    };
    return [
      {
        binding: 'prdnPlntCd',
        header: String(t('mp.label.Factory ID', 'Factory ID')),
        width: 100,
        align: 'center',
      },
      {
        binding: 'prdnPlntNm',
        header: String(t('mp.label.Factory 명', 'Factory 명')),
        width: '*',
        minWidth: 200,
        align: 'left',
      },
      {
        binding: 'horExplCrgeUserNm',
        header: String(t('mp.label.수평전개 담당자', '수평전개 담당자')),
        width: 153,
        align: 'left',
        cssClass: 'WijmoFind WijmoIconButton',
        cellTemplate: (ctx) => `
          <span>${ctx.value || ''}</span>
          ${
            ctx.value
              ? '<Button class="btnHorMgrModal" /><Button class="docu btnHorMgrHistoryModal" />'
              : ''
          }`,
      },
      {
        binding: 'mpItemStatCd',
        visible: false,
      },
      {
        binding: 'mpItemStatCdNm',
        header: String(t('mp.label.상태', '상태')),
        width: 123,
        align: 'center',
      },
      {
        binding: 'countEqp',
        header: String(t('mp.label.대상설비', '대상설비')),
        width: 100,
        align: 'center',
        cellTemplate: (params) => (params.value || 0).toLocaleString(),
      },
      {
        binding: 'detail',
        header: String(t('mp.label.상세보기', '상세보기')),
        width: 70,
        align: 'center',
        cssClass: 'WijmoPlay',
        cellTemplate: (params) => `<Button id="btnDetail" />`,
      },
      {
        binding: 'm4ChgNo',
        header: String(t('mp.label.4MCC 발행번호', '4MCC 발행번호')),
        width: 180,
        align: 'center',
        cssClass: 'WijmoPopup',
        cellTemplate: (params) => `
          <span>${params.item.m4ChgNo || ''}</span>
          ${params.item.m4ChgNo ? '<Button />' : ''}
        `,
      },
      {
        binding: 'm4StatNm',
        header: String(t('mp.label.4MCC 상태', '4MCC 상태')),
        width: 110,
        align: 'center',
      },
      {
        binding: 'm4LvNm',
        header: String(t('mp.label.4MCC Level', '4MCC Level')),
        width: '*',
        minWidth: 200,
        align: 'left',
        cellTemplate: (params) =>
          `${
            props?.defaultMpItem?.ecoYn === 'Y' && params.value && params.value !== 'null'
              ? params.value
              : ''
          }`,
      },
      {
        binding: 'action',
        header: String(t('mp.label.업무 처리', '업무 처리')),
        width: 245, // width: 165,
        align: 'center',
        cssClass: 'WijmoBasicButton noTooltip',
        cellTemplate: drawActionButtons,
      },
      {
        binding: 'dataUpdDtm',
        header: String(t('com.label.최종수정일', '최종수정일')),
        width: 120,
        align: 'center',
      },
      {
        binding: 'execPlanId',
        visible: false,
      },
      {
        binding: 'atchFileGrId',
        visible: false,
      },
      {
        binding: 'horExplCrgeUserId',
        visible: false,
      },
    ];
  }, [
    props?.defaultMpItem?.itemStatCd,
    props?.defaultMpItem?.mpItemTpCd,
    props?.defaultMpItem?.mcrcYn,
    props?.defaultMpItem?.ecoYn,
    hasAuth,
  ]);

  const onInitialized = (grid) => {
    gridRef.current = grid;
    grid.itemFormatter = (panel, row, col, cell) => {
      if (CellType.Cell === panel.cellType) {
        const binding = panel.columns[col].binding;
        const item = panel.rows[row].dataItem;
        // 수평전개담당자 editable css 적용
        if ('horExplCrgeUserNm' === binding) {
          const editable =
            item.horExplCrgeUserId === userId ||
            props?.defaultMpItem?.reqMgrYn === 'Y' ||
            props?.defaultMpItem?.mcrcYn === 'Y' ||
            hasAuth;
          cell.ariaReadOnly =
            !editable ||
            ItemStatCd.Drop === props?.defaultMpItem?.itemStatCd ||
            ItemStatCd.HeHold === props?.defaultMpItem?.itemStatCd;
        }
      }
    };

    grid.hostElement.addEventListener('click', (e) => {
      if (grid.rows.length == 0) return;

      const ht = grid.hitTest(e);
      if (ht.row < 0 || ht.col < 0) return;

      const item = grid.rows[ht.row].dataItem;
      const binding = grid.columns[ht.col].binding;

      if (ht.panel === grid.cells) {
        // 4MCC 발행번호
        if ('m4ChgNo' === binding) {
          if (item.m4ChgNo) {
            open4mcc(item.m4ChgNo);
          }
        }
      }

      if (e.target instanceof HTMLButtonElement) {
        // 수평전개 담당자
        if (e.target.classList.contains('btnHorMgrModal')) {
          if (item.horExplCrgeUserId) {
            const editable =
              item.horExplCrgeUserId === userId ||
              props?.defaultMpItem?.reqMgrYn === 'Y' ||
              props?.defaultMpItem?.mcrcYn === 'Y' ||
              hasAuth;
            if (
              editable &&
              ItemStatCd.Drop !== props?.defaultMpItem?.itemStatCd &&
              ItemStatCd.HeHold !== props?.defaultMpItem?.itemStatCd
            ) {
              setUserModalCondition({
                horExplCrgeUserId: item?.horExplCrgeUserId || '',
                isRequiredRmk: !!item?.mpItemStatCd,
                target: {
                  binding: binding,
                  row: ht.row,
                  col: ht.col,
                },
              });
              setOpenUserModal(true);
            } else {
              setUserReadModalCondition({
                title: t('mp.label.수평전개 담당자', '수평전개 담당자'),
                initParam: item.horExplCrgeUserId,
              });
              setOpenUserReadModal(true);
            }
          }
        }
        // 수평전개 담당자 이력보기
        if (e.target.classList.contains('btnHorMgrHistoryModal')) {
          setMpMgrChgHistoryPopupCondition({
            prdnPlntCd: item.prdnPlntCd,
          });
          setOpenMpMgrChgHistoryPopup(true);
        }
        if ('btnDetail' === e.target.id) {
          setSelectedPlant(item);
        }
        // 설비추가
        else if ('addEqp' === e.target.id) {
          findMpResults(item?.mpItemId, item?.copCd, item?.prdnPlntCd, item?.mpPrdnPlntId).then(
            (result) => {
              setEquipmentModalCondition({
                prdnPlntCd: item?.prdnPlntCd,
                resultList: result,
                eqpChgHisYn: item?.eqpChgHisYn,
                target: {
                  row: ht.row,
                  col: ht.col,
                },
              });
              setOpenEquipmentModal(true);
            }
          );
        }
        // 4M 변경 확정, 추가수평전개, 재신청
        else if (['m4Confirm', 'addHe', 'reHe'].includes(e.target.id)) {
          changeFactoryStatByM4Change(item, e.target.id);
        }
        // TODO IF 처리 전 임시 상태 변경 (삭제예정) - s
        // 수평전개완료
        else if ('cmplHe' === e.target.id) {
          openCommonModal({
            modalType: 'confirm',
            content: 'IF 처리 전 임시 버튼으로 수평전개완료 상태로 변경됩니다. 변경하시겠습니까?',
            yesCallback: () => {
              saveFactoryStat(
                item.mpItemId,
                item.copCd,
                item.prdnPlntCd,
                item.mpPrdnPlntId,
                ItemStatCd.HeComplete
              ).then((res) => {
                if (res.successOrNot === 'Y') {
                  openMessageBar({
                    type: 'confirm',
                    content: t(
                      'mp.label.수평전개완료 처리되었습니다.',
                      '수평전개완료 처리되었습니다'
                    ),
                  });
                  // Item, 법인 상태 재조회
                  props?.onRefresh();
                  findMpItemCop(props.itemId).then((result) => {
                    // 비대상법인은 Tap에서 보이지 않게 수정 - 설비변경점 개선사항 v5.2
                    const cops = (result || []).filter((o) => o.tgtYn === 'Y');
                    setMpItemCops(result);
                    setMpItemTgtYnCops(cops);
                  });
                  searchFactory(item?.mpItemId, item?.copCd, props.itemTpCd).then((result) => {
                    const plnt = (result || []) as MpPlntList[];
                    setRowData(plnt);
                    gridRef.current?.refresh();
                  });
                  // 설비추가한 공장으로 선택 (설비 목록 재조회)
                  setSelectedPlant(item);
                  equipmentRef.current?.searchMpResult(
                    item?.mpItemId,
                    item?.copCd,
                    item?.prdnPlntCd,
                    item?.mpPrdnPlntId
                  );
                }
              });
            },
          });
        }
        // Drop
        else if ('drop' === e.target.id) {
          openCommonModal({
            modalType: 'confirm',
            content: 'IF 처리 전 임시 버튼으로 Drop 상태로 변경됩니다. 변경하시겠습니까?',
            yesCallback: () => {
              saveFactoryStat(
                item.mpItemId,
                item.copCd,
                item.prdnPlntCd,
                item.mpPrdnPlntId,
                ItemStatCd.Drop
              ).then((res) => {
                if (res.successOrNot === 'Y') {
                  openMessageBar({
                    type: 'confirm',
                    content: t('mp.label.Drop 처리되었습니다.', 'Drop 처리되었습니다'),
                  });
                  // Item, 법인 상태 재조회
                  props?.onRefresh();
                  findMpItemCop(props.itemId).then((result) => {
                    // 비대상법인은 Tap에서 보이지 않게 수정 - 설비변경점 개선사항 v5.2
                    const cops = (result || []).filter((o) => o.tgtYn === 'Y');
                    setMpItemCops(result);
                    setMpItemTgtYnCops(cops);
                  });
                  searchFactory(item?.mpItemId, item?.copCd, props.itemTpCd).then((result) => {
                    const plnt = (result || []) as MpPlntList[];
                    setRowData(plnt);
                    gridRef.current?.refresh();
                  });
                  // 설비추가한 공장으로 선택 (설비 목록 재조회)
                  setSelectedPlant(item);
                  equipmentRef.current?.searchMpResult(
                    item?.mpItemId,
                    item?.copCd,
                    item?.prdnPlntCd,
                    item?.mpPrdnPlntId
                  );
                }
              });
            },
          });
        } else if ('dropCopReview' === e.target.id) {
          setDropFactoryCondition(item);
          setOpenMpRemarkPopUp(true);
        }
        // TODO IF 처리 전 임시 상태 변경 (삭제예정) - e
      }
    });
  };

  /**
   * Factory 추가
   */
  const handleAddFactory = () => {
    if (!mpItemCops[copTabIndex]?.copCd) {
      openMessageBar({
        type: 'error',
        content: t('mp.label.선택된 법인코드가 없습니다.', '선택된 법인코드가 없습니다.'),
      });
      return;
    }
    setMpFactoryModalCondition({
      copCd: mpItemCops[copTabIndex]?.copCd,
    });
    setOpenMpFactoryModal(true);
  };

  return (
    <>
      {firstRef.current ? (
        <></>
      ) : (mpItemCops || []).length ? (
        <>
          <Tabs
            value={copTabIndex}
            onChange={(e, newValue) => {
              setCopTabIndex(newValue);
            }}
            css={tabs.subtab}
          >
            {/* 비대상법인은 Tap에서 보이지 않게 수정 - 설비변경점 개선사항 v5.2 */}
            {mpItemTgtYnCops.map((o, idx) => (
              <Tab
                key={o.copCd}
                label={o.copCd}
                id={`mp-cop-tab-${idx}`}
                aria-controls={`mp-cop-tabpanel-${idx}`}
                className={o.tgtYn === 'Y' ? 'hasData' : 'noData'}
              />
            ))}
          </Tabs>
          <TabPanel value={copTabIndex} index={copTabIndex}>
            {/* 법인 하위 수평전개 대상, 상태, 진행율, 비대상 사유 숨기기 - 설비변경점 개선사항 v5.2 */}
            {/* <TableContainer css={tb.table} style={{ marginBottom: '24px' }}>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell>{t('com.label.수평전개 대상', '수평전개 대상')}</TableCell>
                    <TableCell width="80">{mpItemCops[copTabIndex]?.tgtYn || 'N'}</TableCell>
                    <TableCell>{t('com.label.상태', '상태')}</TableCell>
                    <TableCell width="120">
                      {mpItemCops[copTabIndex]?.tgtYn === 'N'
                        ? t('mp.label.비대상', '비대상')
                        : mpItemCops[copTabIndex]?.mpItemStatCdNm ||
                          mpItemCops[copTabIndex]?.mpItemStatCd ||
                          t('mp.label.검토중', '검토중')}
                    </TableCell>
                    <TableCell>{t('com.label.진행율', '진행율')}</TableCell>
                    <TableCell width="100">{`${mpItemCops[copTabIndex]?.progressRate}% (${(
                      mpItemCops[copTabIndex]?.doneCount || 0
                    ).toLocaleString()}/${(
                      mpItemCops[copTabIndex]?.totalCount || 0
                    ).toLocaleString()})`}</TableCell>
                    <TableCell>{t('com.label.미대상 사유', '미대상 사유')}</TableCell>
                    <TableCell>{mpItemCops[copTabIndex]?.unreRsnCtn}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer> */}
            <SubTitleLayout>
              <SubTitleGroup>
                <h3>{t('mp.label.공장 List', '공장 List')}</h3>
                <span className="total">
                  {t('com.label.총', '총')} <span>{(rowData || []).length.toLocaleString()}</span>
                  {t('com.label.건', '건')}
                </span>
                {mpItemCops[copTabIndex]?.copCd && (
                  <div className="info warning">{mpItemCops[copTabIndex]?.copCd}</div>
                )}
              </SubTitleGroup>
              {editableFactory && (
                <ControlBtnGroup>
                  <Button
                    css={IconButton.button}
                    className="setting"
                    onClick={handleAddFactory}
                    disableRipple
                  >
                    {t('mp.label.Factory추가', 'Factory추가')}
                  </Button>
                </ControlBtnGroup>
              )}
            </SubTitleLayout>
            <CustomGrid
              layoutDefinition={layoutDefinition}
              rowData={rowData}
              height={130}
              excludeFilter={['detail', 'action']}
              isReadOnly={true}
              isSelector={false}
              allowPinning={false}
              initialized={onInitialized}
            />
            <MpItemOtherContentEquipmentGrid
              ref={equipmentRef}
              mpItemId={selectedPlant?.mpItemId}
              copCd={selectedPlant?.copCd}
              mpPrdnPlntId={selectedPlant?.mpPrdnPlntId}
              prdnPlntCd={selectedPlant?.prdnPlntCd}
              prdnPlntNm={selectedPlant?.prdnPlntNm}
            />
          </TabPanel>
        </>
      ) : (
        <>
          <h2>
            {t(
              'mp.label.대상법인이 없어 조회할 법인이 없습니다.',
              '대상법인이 없어 조회할 법인이 없습니다.'
            )}
          </h2>
        </>
      )}
      {/*담당자 적용*/}
      {isOpenUserModal && (
        <UserMultiModal
          open={isOpenUserModal}
          close={() => setOpenUserModal(false)}
          title={t('mp.label.담당자 조회', '담당자 조회')}
          singleSelect={true}
          isOnlyUsed={true}
          rmk={userModalCondition?.isRequiredRmk}
          isRequireRmk={userModalCondition?.isRequiredRmk}
          defaultUserId={userModalCondition?.horExplCrgeUserId}
          condition={{
            userId: userModalCondition?.horExplCrgeUserId || '',
          }}
          onCallback={(result: any) => {
            const target = userModalCondition.target;
            if (target && target?.row > -1) {
              const item = gridRef.current?.rows[target.row].dataItem;
              saveFactory(
                item.mpItemId,
                item.copCd,
                item.mpPrdnPlntId,
                result.userId,
                result.rmk
              ).then((res) => {
                if (res.successOrNot === 'Y') {
                  item.horExplCrgeUserId = result.userId;
                  item.horExplCrgeUserNm = result.empNm;
                  gridRef.current?.collectionView.refresh();
                  openMessageBar({
                    type: 'confirm',
                    content: t('com.label.저장되었습니다.', '저장되었습니다.'),
                  });
                  // 공장 자동처리 후 재조회
                  searchFactory(
                    props.itemId || '',
                    mpItemCops[copTabIndex]?.copCd || '',
                    props.itemTpCd
                  ).then((result) => {
                    const plnt = (result || []) as MpPlntList[];
                    setRowData(plnt);
                  });
                } else {
                  openMessageBar({
                    type: 'error',
                    content:
                      res.data && typeof res.data === 'string'
                        ? res.data
                        : t(
                            'com.label.저장 중 오류가 발생했습니다.',
                            '저장 중 오류가 발생했습니다.'
                          ),
                  });
                }
              });
            }
          }}
        />
      )}
      {isOpenFileUploadModal && (
        <FileUploadPopUp
          open={isOpenFileUploadModal}
          close={() => setOpenFileUploadModal(false)}
          initParam={{
            atchFileGrId: fileUploadModalCondition.atchFileGrId,
            atchFileTpCd: fileUploadModalCondition.atchFileTpCd,
            optValCtn1: fileUploadModalCondition.tableName,
            bizName: fileUploadModalCondition.bizName,
          }}
          onCallback={(atchFileGrId, fileCount) => {
            const target = fileUploadModalCondition.target;
            if (target) {
              gridRef.current?.setCellData(target.row, target.col, fileCount);
              gridRef.current?.startEditing(false, target.row, target.col);
              gridRef.current?.finishEditing();
            }
            setOpenFileUploadModal(false);
          }}
        />
      )}

      {isOpenUserReadModal && (
        <GatingContListPopUp
          open={isOpenUserReadModal}
          close={() => setOpenUserReadModal(false)}
          title={userReadModalCondition?.title}
          initParam={userReadModalCondition?.initParam}
        />
      )}

      {isOpenMpFactoryModal && (
        <MpFactoryPopUp
          open={isOpenMpFactoryModal}
          close={() => setOpenMpFactoryModal(false)}
          condition={{ copCd: mpFactoryModalCondition?.copCd || '', rowData: rowData || [] }}
          onCallbackData={(result) => {
            const addRows = (result || []).reduce(
              (acc, cur) => [
                ...acc,
                {
                  crudKey: CrudCode.CREATE,
                  mpItemId: props.itemId,
                  prdnPlntCd: cur.prdnPlntCd,
                  prdnPlntNm: cur.prdnPlntNm,
                  copCd: mpItemCops[copTabIndex]?.copCd,
                  horExplCrgeUserId: cur.horExplCrgeUserId,
                  horExplCrgeUserNm: cur.horExplCrgeUserNm,
                  horExplYn: 'Y',
                  chgRsnCtn: cur.rmk,
                },
              ],
              [] as MpPlntList[]
            );

            // 공장 지정시 자동저장
            // [25.11.10] 팝업 내부로 알람메시지 이동
            // if (addRows.length < 1) {
            //   openMessageBar({
            //     type: 'error',
            //     content: t('com.label.추가된 항목이 없습니다.', '추가된 항목이 없습니다.'),
            //   });
            //   return false;
            // }

            saveFactoryList(props.itemId, mpItemCops[copTabIndex]?.copCd, addRows).then((res) => {
              if (res.successOrNot === 'Y') {
                openMessageBar({
                  type: 'confirm',
                  content: t('com.label.저장되었습니다.', '저장되었습니다.'),
                });
                // 공장 자동처리 후 재조회
                searchFactory(
                  props.itemId || '',
                  mpItemCops[copTabIndex]?.copCd || '',
                  props.itemTpCd
                ).then((result) => {
                  const plnt = (result || []) as MpPlntList[];
                  setRowData(plnt);
                  // 조회 후 첫번째 항목 선택
                  if ((plnt || []).length > 0) {
                    setSelectedPlant(plnt[0]);
                  }
                });
              } else {
                openMessageBar({
                  type: 'error',
                  content:
                    res.data && typeof res.data === 'string'
                      ? res.data
                      : t('com.label.저장 중 오류가 발생했습니다.', '저장 중 오류가 발생했습니다.'),
                });
              }
            });
          }}
        />
      )}

      {/* 대상 설비 선택 */}
      {isOpenEquipmentModal &&
        mpItemCops[copTabIndex]?.copCd &&
        equipmentModalCondition?.prdnPlntCd && (
          <EquipmentPopUp
            open={isOpenEquipmentModal}
            close={() => setOpenEquipmentModal(false)}
            title={t('mp.label.설비추가', '설비추가')}
            condition={{
              copCd: mpItemCops[copTabIndex]?.copCd,
              prdnPlntCd: equipmentModalCondition?.prdnPlntCd,
              // processCode: props?.defaultMpItem?.procCd, //25.11.10 조건 전체로 설정을 위해 주석처리 (PI 요청)
              equipmentGroup: props?.defaultMpItem?.eqclId,
              equipmentLevel: `${EquipmentLevel.MACHINE},${EquipmentLevel.MAIN}`, // [25.09.01] mp 설비대상선택시 기본값 머신으로 변경(사용자테스트 후 개선요청)
              equipmentLevels: [EquipmentLevel.MACHINE, EquipmentLevel.MAIN], // CSR E20251027-000124 MAIN 추가요청
            }}
            isMpItem={true}
            isCopCdFix={true}
            isPrdnPlntFix={true}
            // isFixedMainLevel={true} // [25.07.28] 설비추가 시 설비레벨 M,C,U까지 가능
            isCortCrgeUser={true}
            singleSelect={false}
            editable={false}
            isChgRsnCtn={equipmentModalCondition?.eqpChgHisYn === 'Y'}
            defaultEquipment={(equipmentModalCondition?.resultList || []).reduce(
              (acc, cur) => [
                ...acc,
                {
                  ...cur,
                  equipmentId: cur.eqpId,
                  equipmentName: cur.equipmentName,
                  equipmentDesc: cur.equipmentName,
                  cortCrgeUserId: cur.cortCrgeUserId,
                  cortCrgeUserNm: cur.cortCrgeUserNm,
                  equipmentGroup: cur.equipmentGroup,
                  copCd: cur.copCd,
                  prdnPlntCd: cur.plntCd,
                  prdnPlntNm: cur.plntNm,
                  factoryCode: cur.factoryCode,
                  processCode: cur.processCode,
                  chgRsnCtn: cur.chgRsnCtn,
                },
              ],
              [] as CustomEquipment[]
            )}
            onCallback={(result) => {
              const target = equipmentModalCondition.target;
              if (target && target?.row > -1) {
                const item = gridRef.current?.rows[target.row].dataItem;
                const equipments = (result?.eqpList || []) as CustomEquipment[];
                equipmentRef.current?.saveEquipment(item, equipments, result?.remark).then(() => {
                  searchFactory(item?.mpItemId, item?.copCd, props.itemTpCd).then((result) => {
                    const plnt = (result || []) as MpPlntList[];
                    setRowData(plnt);
                  });
                  // 설비추가한 공장으로 선택 (설비 목록 재조회)
                  setSelectedPlant(item);
                  equipmentRef.current?.searchMpResult(
                    item?.mpItemId,
                    item?.copCd,
                    item?.prdnPlntCd,
                    item?.mpPrdnPlntId
                  );
                  gridRef.current?.refresh();
                });
              }
            }}
          />
        )}

      {isOpenMpMgrChgHistoryPopup &&
        props?.defaultMpItem?.itemId &&
        mpMgrChgHistoryPopupCondition?.prdnPlntCd && (
          <MpMgrChgHistoryPopUp
            open={isOpenMpMgrChgHistoryPopup}
            close={() => setOpenMpMgrChgHistoryPopup(false)}
            title={String(t('mp.label.수평전개담당자', '수평전개담당자'))}
            condition={{
              mpItemId: props?.defaultMpItem?.itemId,
              mgrTpCd: 'HOR',
              prdnPlntCd: mpMgrChgHistoryPopupCondition?.prdnPlntCd,
            }}
          />
        )}
      {/* 25.11.03 설비변경점 개선사항 v5.2 4MCC 확정전 DROP */}
      {isOpenMpRemarkPopUp && (
        <MpRemarkPopUp
          title={t('mp.label.Drop', 'Drop')}
          initParam={{
            rmk: '',
            typeValue: '',
          }}
          open={isOpenMpRemarkPopUp}
          onCallbackData={(value) => {
            // MP ITEM STAT HIS 변경사유 조건
            const mpItemCondition: MpItem = {
              itemId: props.defaultMpItem.itemId,
              itemStatCd: props.defaultMpItem.itemStatCd,
              mpItemTpCd: props.defaultMpItem.mpItemTpCd,
              rmk: value.rmk,
            };

            // DROP 로직
            saveFactoryStat(
              dropFactoryCondition.mpItemId,
              dropFactoryCondition.copCd,
              dropFactoryCondition.prdnPlntCd,
              dropFactoryCondition.mpPrdnPlntId,
              ItemStatCd.Drop,
              mpItemCondition
            ).then((res) => {
              if (res.successOrNot === 'Y') {
                openMessageBar({
                  type: 'confirm',
                  content: t('mp.label.Drop 처리되었습니다.', 'Drop 처리되었습니다'),
                });
                // Item, 법인 상태 재조회
                props?.onRefresh();
                findMpItemCop(props.itemId).then((result) => {
                  // 비대상법인은 Tap에서 보이지 않게 수정 - 설비변경점 개선사항 v5.2
                  const cops = (result || []).filter((o) => o.tgtYn === 'Y');
                  setMpItemCops(result);
                  setMpItemTgtYnCops(cops);
                });
                searchFactory(
                  dropFactoryCondition?.mpItemId,
                  dropFactoryCondition?.copCd,
                  props.itemTpCd
                ).then((result) => {
                  const plnt = (result || []) as MpPlntList[];
                  setRowData(plnt);
                  gridRef.current?.refresh();
                });
                // 설비추가한 공장으로 선택 (설비 목록 재조회)
                setSelectedPlant(dropFactoryCondition);
                equipmentRef.current?.searchMpResult(
                  dropFactoryCondition?.mpItemId,
                  dropFactoryCondition?.copCd,
                  dropFactoryCondition?.prdnPlntCd,
                  dropFactoryCondition?.mpPrdnPlntId
                );
              }
            });
          }}
          close={() => setOpenMpRemarkPopUp(false)}
        />
      )}
    </>
  );
};

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function TabPanel(props: { [x: string]: any; children: any; value: any; index: any }) {
  const { children, value, index } = props;

  return (
    <div role="tabpanel" hidden={value !== index} id={`mp-cop-tabpanel-${index}`}>
      {value === index && <div>{children}</div>}
    </div>
  );
}

export default React.forwardRef(MpItemOtherContent);
